import Immutable from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import { DocId } from 'records/Doc';
import { Page, PageId } from 'records/Page';
import { PageReadyForReview } from 'records/PageReadyForReview';
import {
  getPages,
  getPagesReadyForReview,
  getPageThumbnailUrl,
} from 'services/pageService';

export interface PageActions {
  getPages: (
    ActionParams: AppStateActionParams,
    docId: DocId
  ) => Promise<AppState>;

  getFirstPage: (
    ActionParams: AppStateActionParams,
    docId: DocId
  ) => Promise<AppState>;

  getPagesForAllDocuments: (
    actionParams: AppStateActionParams
  ) => Promise<AppState>;

  getPagesReadyForReview: (
    actionParams: AppStateActionParams,
    processingRequestid: string
  ) => Promise<AppState>;

  setSelectedPages: (
    actionParams: AppStateActionParams,
    selectedPages: Immutable.Set<PageId>
  ) => AppState;

  resetSelectedPages: (actionParams: AppStateActionParams) => AppState;

  clearPages: (actionParams: AppStateActionParams) => AppState;
}

const pageActions: PageActions = {
  getPages: async (
    { state }: AppStateActionParams,
    docId: DocId
  ): Promise<AppState> => {
    const pages = await getPages(docId);

    const r = pages.map(async (p) => {
      const url = await getPageThumbnailUrl(docId, p.pageid);

      return p.set('thumbnailUrl', url);
    });

    const pagesWithUrl = await Promise.all(r);

    return pagesWithUrl.reduce(
      (acc, p) => acc.setIn(['pages', p.pageid], p),
      state()
    );
  },

  getFirstPage: async (
    { state }: AppStateActionParams,
    docId: DocId
  ): Promise<AppState> => {
    const pages = await getPages(docId);

    let page = pages.get(0);
    if (page) {
      const url = await getPageThumbnailUrl(docId, page.pageid);
      page = page.set('thumbnailUrl', url);
      return state().setIn(['pages', page.pageid], page);
    }

    return state();
  },

  getPagesForAllDocuments: async ({
    state,
  }: AppStateActionParams): Promise<AppState> => {
    const { documents } = state();

    const requests = [...documents.values()].map(async (d) => {
      const pages = await getPages(d.documentid);

      const r = pages.map(async (p) => {
        const url = await getPageThumbnailUrl(d.documentid, p.pageid);

        return p.set('thumbnailUrl', url);
      });

      const pagesWithUrl = await Promise.all(r);

      return pagesWithUrl;
    });

    const resp = await Promise.all(requests);

    return state().update('pages', (pages) => {
      return resp.flat().reduce((acc, p) => acc.set(p.pageid, p), pages);
    });
  },

  getPagesReadyForReview: async (
    { state }: AppStateActionParams,
    processingRequestid: string
  ): Promise<AppState> => {
    let pagesReadyForReview = await getPagesReadyForReview(processingRequestid);

    const sheetNumberMap = new Map<string, number>();
    pagesReadyForReview = pagesReadyForReview.map((p) => {
      const cnt = sheetNumberMap.get(p.inferredsheetnumber);
      if (cnt === undefined) {
        sheetNumberMap.set(p.inferredsheetnumber, 1);
        return p;
      }
      sheetNumberMap.set(p.inferredsheetnumber, cnt + 1);
      return p.set('inferredsheetnumber', `${p.inferredsheetnumber} (${cnt})`);
    });

    const dupedPubReqs = pagesReadyForReview.reduce(
      ({ temp, result }, pr) => {
        if (temp.has(pr.inferredsheetnumber)) {
          result.add(pr.inferredsheetnumber);
        } else {
          temp.add(pr.inferredsheetnumber);
        }

        return { temp, result };
      },
      { temp: new Set<string>(), result: new Set<string>() }
    ).result;

    const m = pagesReadyForReview
      .sort((a, b) => {
        const aDuped = dupedPubReqs.has(a.inferredsheetnumber);
        const bDuped = dupedPubReqs.has(b.inferredsheetnumber);
        if (aDuped && !bDuped) return -1;
        if (!aDuped && bDuped) return 1;
        if (a.pageIndex < b.pageIndex) return -1;
        if (a.pageIndex > b.pageIndex) return 1;
        return 0;
      })
      .reduce(
        (acc, prfr) => acc.set(prfr.pageid, prfr),
        Immutable.OrderedMap<PageId, PageReadyForReview>()
      );

    return state().set('pagesReadyForReview', m);
  },

  setSelectedPages: (
    { state }: AppStateActionParams,
    selectedPages: Immutable.Set<PageId>
  ): AppState => {
    return state().set('selectedPages', selectedPages);
  },

  resetSelectedPages: ({ state }: AppStateActionParams): AppState => {
    return state().set('selectedPages', Immutable.Set());
  },

  clearPages: ({ state }: AppStateActionParams): AppState => {
    return state().set('pages', Immutable.OrderedMap<PageId, Page>());
  },
};

export default pageActions;
